(() => {
  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-mot-navigation]').forEach((root) => {
      if (root.dataset.initialized) return;
      root.dataset.initialized = 'true';
      root.classList.add('is-enhanced');
      const toggle = root.querySelector('[data-mot-toggle]');
      const navigation = root.querySelector('.mot-nav__navigation');
      const shade = root.querySelector('[data-mot-dismiss]');
      const details = Array.from(root.querySelectorAll('.mot-nav__details'));
      const desktop = window.matchMedia('(min-width: 1024px)');
      const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
      const listeners = new AbortController();
      const options = { signal: listeners.signal };
      let hoverTimer;
      let editorSelectedDetail = null;
      let mobilePageTop = null;
      const measure = () => {
        root.classList.toggle('is-scrolled', window.scrollY > 40);
        const viewportHeight = window.visualViewport?.height || window.innerHeight;
        const position = navigation.getBoundingClientRect();
        const headerBottom = root.getBoundingClientRect().bottom;
        const panelGap = parseFloat(getComputedStyle(root).getPropertyValue('--mot-nav-dropdown-gap')) || 0;
        const top = desktop.matches ? headerBottom + panelGap : position.height ? position.top : headerBottom + 6;
        root.style.setProperty('--mot-nav-shade-top', `${headerBottom}px`);
        root.style.setProperty('--mot-nav-panel-max', `${Math.max(120, viewportHeight - top - 12)}px`);
        const section = root.closest('header-section');
        if (section?.dataset.sticky === 'true') document.documentElement.style.setProperty('--sticky-header-height', `${section.getBoundingClientRect().height}px`);
      };
      const update = () => {
        const active = details.find((detail) => detail.open);
        const mobileOpen = !desktop.matches && root.classList.contains('is-mobile-open');
        root.classList.toggle('is-drilled', Boolean(active) && !desktop.matches);
        root.classList.toggle('is-help', Boolean(active?.classList.contains('mot-nav__help')) && !desktop.matches);
        toggle.setAttribute('aria-expanded', String(mobileOpen));
        shade.hidden = !mobileOpen;
        document.documentElement.classList.toggle('mot-navigation-open', mobileOpen);
        measure();
      };
      const close = (restoreFocus = false) => {
        clearTimeout(hoverTimer);
        const active = details.find((detail) => detail.open);
        details.forEach((detail) => { detail.open = false; });
        root.classList.remove('is-mobile-open');
        mobilePageTop = null;
        navigation.scrollTop = 0;
        update();
        if (restoreFocus) {
          if (desktop.matches) active?.querySelector('summary').focus({ preventScroll: true });
          else toggle.focus({ preventScroll: true });
        }
      };
      const openDetail = (detail, focusBack = false) => {
        details.forEach((other) => { if (other !== detail) other.open = false; });
        detail.open = true;
        navigation.scrollTop = 0;
        update();
        if (focusBack && !desktop.matches) detail.querySelector('[data-mot-back]')?.focus({ preventScroll: true });
      };
      toggle.addEventListener('click', () => {
        if (root.classList.contains('is-mobile-open')) close(true);
        else { mobilePageTop = window.scrollY; root.classList.add('is-mobile-open'); update(); }
      }, options);
      shade.addEventListener('click', () => close(true), options);
      details.forEach((detail) => {
        const summary = detail.querySelector('summary');
        summary.addEventListener('click', (event) => {
          event.preventDefault();
          const destination = summary.dataset.motDestination;
          if (desktop.matches && destination && destination !== '#') {
            window.location.assign(destination);
            return;
          }
          if (detail.open) { detail.open = false; update(); }
          else openDetail(detail, event.detail === 0);
        }, options);
        detail.querySelector('[data-mot-back]')?.addEventListener('click', () => {
          detail.open = false;
          navigation.scrollTop = 0;
          update();
          summary.focus({ preventScroll: true });
        }, options);
        detail.closest('li')?.addEventListener('pointerenter', () => {
          if (!desktop.matches || !finePointer.matches) return;
          clearTimeout(hoverTimer);
          openDetail(detail);
        }, options);
      });
      document.addEventListener('shopify:block:select', (event) => {
        const detail = event.target.closest('.mot-nav__details');
        if (!detail || !root.contains(detail)) return;
        editorSelectedDetail = detail;
        if (!desktop.matches) root.classList.add('is-mobile-open');
        openDetail(detail);
      }, options);
      document.addEventListener('shopify:block:deselect', () => { editorSelectedDetail = null; }, options);
      root.addEventListener('pointerenter', () => clearTimeout(hoverTimer), options);
      root.querySelectorAll('.mot-nav__direct').forEach((item) => {
        item.addEventListener('pointerenter', () => {
          if (desktop.matches && finePointer.matches) close();
        }, options);
      });
      root.addEventListener('pointerleave', () => {
        if (!editorSelectedDetail && desktop.matches && finePointer.matches) hoverTimer = setTimeout(() => close(), 180);
      }, options);
      root.addEventListener('click', (event) => {
        const categoryLink = event.target.closest('[data-mot-category]');
        if (categoryLink && !desktop.matches) {
          const detail = details.find((item) => item.dataset.motCategoryId === categoryLink.dataset.motCategory);
          if (detail) { event.preventDefault(); openDetail(detail, true); }
        }
        if (event.target.closest('.mot-nav__search>div>a, .mot-nav__actions>a, .mot-nav__navigation a[href], [data-mot-highlights-toggle]')) close();
      }, options);
      document.addEventListener('click', (event) => {
        if (!root.contains(event.target)) close();
      }, options);
      root.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          const active = details.find((detail) => detail.open);
          if (active && !desktop.matches) {
            event.preventDefault();
            active.open = false;
            navigation.scrollTop = 0;
            update();
            active.querySelector('summary').focus({ preventScroll: true });
          } else if (active || root.classList.contains('is-mobile-open')) {
            event.preventDefault();
            close(true);
          }
        }
        if (event.key === 'Tab' && !desktop.matches && root.classList.contains('is-mobile-open')) {
          const focusable = Array.from(root.querySelectorAll('a[href],button,summary,input,select')).filter((element) => !element.disabled && element.getClientRects().length && !element.closest('[hidden]') && element !== shade);
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus({ preventScroll: true }); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus({ preventScroll: true }); }
        }
      }, options);
      desktop.addEventListener('change', () => close(), options);
      window.addEventListener('resize', measure, options);
      window.visualViewport?.addEventListener('resize', measure, options);
      window.addEventListener('scroll', () => {
        if (mobilePageTop !== null && Math.abs(window.scrollY - mobilePageTop) > .5) {
          window.scrollTo({ top: mobilePageTop, behavior: 'instant' });
        }
        measure();
      }, { ...options, passive: true });
      const resizeObserver = new ResizeObserver(measure);
      resizeObserver.observe(root);
      document.addEventListener('shopify:section:unload', (event) => {
        if (!event.target.contains(root)) return;
        close();
        listeners.abort();
        resizeObserver.disconnect();
      }, options);
      update();
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();

(() => {
  const scrollWorkspaceHash = () => {
    if (location.pathname !== '/collections/office-goods') return;
    const labels = {'#pinboards':'Pinboards','#monitor-stand':'Monitor Stand','#magnetic-boards':'Magnetic Boards','#office-goods-accessories':'Office Goods Accessories'};
    const label = labels[location.hash];
    if (!label) return;
    const heading = Array.from(document.querySelectorAll('main h2')).find(el => el.textContent.trim() === label);
    if (!heading) return;
    heading.id = location.hash.slice(1);
    const header = document.querySelector('[data-theme-header]');
    const offset = (header?.getBoundingClientRect().height || 70) + 10;
    window.scrollTo({top:window.scrollY + heading.getBoundingClientRect().top - offset,behavior:'smooth'});
  };
  window.addEventListener('hashchange',scrollWorkspaceHash);
  if(document.readyState === 'complete') setTimeout(scrollWorkspaceHash,60);
  else window.addEventListener('load',()=>setTimeout(scrollWorkspaceHash,60),{once:true});
})();

// Keep section destinations consistent when Shopify serves cached navigation data.
(() => {
 const destinations = {"Wavy Shelves":"/collections/shelving#fold-wavy-shelves","Bedside Tables":"/collections/shelving#fold-bedside-tables","Layout Shelves":"/collections/shelving#fold-layout-shelves","Round Shelves":"/collections/shelving#fold-round-shelves","Sidekick Journals":"/collections/paper-goods#sidekick-journals","Planners & Journals":"/collections/paper-goods#daily-weekly-monthly-planners","Undated Planners":"/collections/paper-goods#three-monthly-planners","Stationery & Accessories":"/collections/paper-goods#paper-goods-accessories"};
 const restoreSectionLinks = () => {
 document.querySelectorAll(".mot-nav__navigation a").forEach(link => {
 const destination = destinations[link.textContent.trim()];
 if (destination) link.setAttribute("href", destination);
 });
 };
 if(document.readyState === "loading") document.addEventListener("DOMContentLoaded",restoreSectionLinks,{once:true});
 else restoreSectionLinks();
 document.addEventListener("shopify:section:load",restoreSectionLinks);
})();



(() => {
  const key='mot-highlights-clicked';
  const reduced=window.matchMedia('(prefers-reduced-motion: reduce)');
  let clicked=false,timer,frame;
  let activePaths=[];
  try { clicked=sessionStorage.getItem(key)==='1'; } catch (_) {}
  const original='M14.2 21c3.2-1.1 5.3-3.9 5.3-7.3 0-4.5-3.8-8.5-7.8-11.2-.6 4.1-3.2 6.1-5.2 9.1-2.5 3.7-1.4 7.5 2 9.4-1.2-2.9.1-5 1.9-7.2.8 1.4 1.8 2.1 2.8 2.7.7-1.8.6-3.5-.1-5.8 3.2 2.8 3.7 6.8 1.1 10.3Z';
  const numbers=original.match(/[-+]?(?:\d*\.\d+|\d+)/g).map(Number);
  let x=numbers[0],y=numbers[1];
  const points=[[x,y]];
  for(let i=2;i<numbers.length;i+=6){
    const ax=x+numbers[i],ay=y+numbers[i+1],bx=x+numbers[i+2],by=y+numbers[i+3],cx=x+numbers[i+4],cy=y+numbers[i+5];
    for(let j=1;j<=32;j++){
      const t=j/32,u=1-t;
      points.push([u*u*u*x+3*u*u*t*ax+3*u*t*t*bx+t*t*t*cx,u*u*u*y+3*u*u*t*ay+3*u*t*t*by+t*t*t*cy]);
    }
    x=cx;y=cy;
  }

  const restore=()=>{
    cancelAnimationFrame(frame);
    activePaths.forEach(path=>path.setAttribute('d',original));
    activePaths=[];
  };
  const stop=()=>{clearInterval(timer);restore();};
  const flicker=()=>{
    if(clicked||reduced.matches||document.hidden)return;
    restore();
    activePaths=Array.from(document.querySelectorAll('.mot-nav__highlights svg path'));
    if(!activePaths.length)return;
    let start=null;
    const draw=now=>{
      if(clicked||reduced.matches||document.hidden){restore();return;}
      if(start===null)start=now;
      const elapsed=Math.min((now-start)/1000,2);
      if(elapsed>=2){restore();return;}
      const phase=elapsed*.85;
      const smooth=v=>v*v*(3-2*v);
      const envelope=smooth(Math.min(elapsed/.2,1))*smooth(Math.max(0,Math.min((2-elapsed)/.35,1)));
      const rhythm=phase*3.7+.7*Math.sin(phase*1.27)+.24*Math.sin(phase*4.31);
      const exchange=(1-Math.cos(rhythm))/2;
      const coordinates=points.map(([px,py],i)=>{
        const opposite=points[i<=128?128-i:384-i];
        const mx=px*(1-exchange)+(24-opposite[0])*exchange;
        const my=py*(1-exchange)+opposite[1]*exchange;
        const height=Math.max(0,(21-my)/19);
        const inner=i>128;
        const curl=Math.sin(phase*7.1-height*5.2)+.36*Math.sin(phase*12.7-height*8.1);
        const draft=1.45*Math.sin(rhythm-.75)+.6*Math.sin(phase*2.19);
        const flow=(draft+curl*.95)*Math.pow(height,1.45);
        const pulse=.055*Math.sin(phase*8.3)+.035*Math.sin(phase*13.1);
        const width=1-pulse*Math.sin(height*Math.PI);
        const rise=(Math.sin(phase*6.7-height*3.1)*.62+.27*Math.sin(phase*11.9-height*5))*height;
        const tongue=inner?Math.sin(phase*5.3+(i-128)/128*4.2)*1.05*Math.sin((i-128)/128*Math.PI):0;
        const fx=px+(12+(mx-12)*width+flow-px)*.65*envelope;
        const fy=py+(my+rise-tongue-py)*.65*envelope;
        return (i===0?'M':'L')+fx.toFixed(3)+' '+fy.toFixed(3);
      });

      const shape=coordinates.join(' ')+'Z';
      activePaths.forEach(path=>path.setAttribute('d',shape));
      frame=requestAnimationFrame(draw);
    };
    frame=requestAnimationFrame(draw);
  };
  const schedule=()=>{stop();if(!clicked&&!reduced.matches)timer=setInterval(flicker,10000);};
  document.addEventListener('click',event=>{
    if(!(event.target instanceof Element)||!event.target.closest('[data-mot-highlights-toggle]'))return;
    clicked=true;
    try {sessionStorage.setItem(key,'1');} catch (_) {}
    stop();
  },true);
  document.addEventListener('visibilitychange',()=>{if(document.hidden)restore();});
  reduced.addEventListener('change',schedule);
  schedule();
})();
